import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Textarea,
  HStack,
  VStack,
  Icon,
  Box,
  useToast,
} from '@chakra-ui/react';
import { FaStar } from 'react-icons/fa';
import { BACKEND_API } from '../../constants/backendApiEndpoints';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  pipelineType: string;
  onSubmitSuccess?: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  jobId,
  pipelineType,
  onSubmitSuccess,
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: 'Please select a rating',
        description: 'Click on the stars to rate your experience',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(BACKEND_API.FEEDBACK.SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          job_id: jobId,
          rating,
          review_text: reviewText.trim() || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      toast({
        title: 'Thank you for your feedback!',
        description: 'Your review has been submitted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSubmitSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit feedback. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const getPipelineDisplayName = (type: string): string => {
    const names: Record<string, string> = {
      VCF2TXT: 'VCF to TXT Converter',
      HLA: 'HLA Typing',
      PROPEX: 'PGx Extractor',
      'Haplotype Caller': 'Haplotype Caller',
    };
    return names[type] || type;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleSkip} isCentered size="md">
      <ModalOverlay bg="rgba(26, 18, 98, 0.4)" />
      <ModalContent borderRadius="16px" mx={4}>
        <ModalHeader color="primary.500" fontWeight="bold" textAlign="center" pb={0}>
          How was your experience?
        </ModalHeader>
        <ModalCloseButton color="muted.400" />
        
        <ModalBody pt={2}>
          <VStack spacing={4}>
            <Text color="muted.500" fontSize="sm" textAlign="center">
              Rate your experience with {getPipelineDisplayName(pipelineType)}
            </Text>
            
            {/* Star Rating */}
            <HStack spacing={2} justify="center">
              {[1, 2, 3, 4, 5].map((star) => (
                <Box
                  key={star}
                  cursor="pointer"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  transition="transform 0.15s ease"
                  _hover={{ transform: 'scale(1.2)' }}
                >
                  <Icon
                    as={FaStar}
                    boxSize={8}
                    color={
                      star <= (hoverRating || rating)
                        ? 'yellow.400'
                        : 'gray.300'
                    }
                    transition="color 0.15s ease"
                  />
                </Box>
              ))}
            </HStack>
            
            {rating > 0 && (
              <Text fontSize="sm" color="primary.500" fontWeight="medium">
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </Text>
            )}
            
            {/* Review Text (Optional) */}
            <Box w="100%">
              <Text fontSize="sm" color="muted.500" mb={2}>
                Share your thoughts (optional)
              </Text>
              <Textarea
                placeholder="What did you like? What could be improved?"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                resize="none"
                rows={3}
                maxLength={2000}
                focusBorderColor="primary.500"
              />
              <Text fontSize="xs" color="muted.400" textAlign="right" mt={1}>
                {reviewText.length}/2000
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3} justifyContent="center">
          <Button
            variant="ghost"
            onClick={handleSkip}
            isDisabled={isSubmitting}
            color="muted.500"
          >
            Skip
          </Button>
          <Button
            bg="primary.500"
            color="white"
            _hover={{ bg: 'primary.600' }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={rating === 0}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FeedbackModal;
